import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    DataGrid,
    GridColDef,
    GridFilterModel,
    GridPaginationModel,
    GridRowSelectionModel,
    GridSlots,
    GridSortModel,
} from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';

import { GridColType } from '@mui/x-data-grid/models/colDef/gridColType';
import {
    DeviceWithSticks,
    OnboardDeviceConnectionStatus,
    OnboardDevicesPagination,
    OnboardDevicesQueryParams,
} from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { formatDate } from '../utils/formatDate';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loadOnboardDevices,
    selectSelectedOnboardDeviceTableRows,
    setSelectedOnboardDeviceTableRows,
} from '../store/devices.reducer';
import {
    loadOnboardDevicesHardwareConfigurations,
    selectOnboardDevicesHardwareConfigurations,
} from '../store/onboardDeviceHardwareConfigurations.reducer';
import { loadOrganizations } from '../store/organizations.reducer';
import { useManageGrid } from '../hooks/useManageGrid';
import { useMatrixCodeScanner } from '../hooks/useMatrixCodeScanner';
import {
    ONBOARD_DEVICE_SERVER_SORT_FIELDS,
    buildOnboardDeviceRows,
    buildOnboardDevicesFiltersFromGridFilterModel,
    getOnboardDevicesSortQueryPatch,
    getSelectedRowsForDevices,
    isOnboardDeviceServerFilterable,
    isOnboardDeviceServerSortable,
    withScannedCodesFilter,
} from '../utils/onboardDevicesTable';
import { getOnboardDeviceFilterOperators } from './GridFilters/onboardDeviceFilterOperators';
import { ActivationSticksCell } from './GridCells/ActivationSticksCell';
import { ChecksCell } from './GridCells/ChecksCell';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { DeviceSerialStatusCell } from './GridCells/DeviceSerialStatusCell';
import { OrganizationLabelCell } from './GridCells/OrganizationLabelCell';
import { RoadMapCell } from './GridCells/RoadMapCell';
import { UpToDateCell } from './GridCells/UpToDateCell';
import { VersionsCell } from './GridCells/VersionsCell';
import { OnboardDevicesToolbar } from './OnboardDevicesToolbar';

interface OnboardDevicesTableGridProps {
    devices: DeviceWithSticks[];
    pagination: OnboardDevicesPagination;
    loading: boolean;
    error: string | null;
    query: OnboardDevicesQueryParams;
    onQueryChange: (queryPatch: Partial<OnboardDevicesQueryParams>) => void;
    onlineStatusByDeviceId?: Record<string, OnboardDeviceConnectionStatus>;
}

const ONBOARD_DEVICE_COLUMN_DEFS = [
    { key: 'deviceSerial', type: 'number', renderCell: DeviceSerialStatusCell },
    { key: 'deviceId', renderCell: CopyTextCell },
    { key: 'organization', renderCell: OrganizationLabelCell },
    { key: 'status', renderCell: CopyTextCell },
    { key: 'routeCard', headerName: 'Road Map' },
    { key: 'vpnOnlineAt', headerName: 'Last Online', valueFormatter: formatDate },
    { key: 'systemVersion', renderCell: VersionsCell },
    { key: 'hardwareConfiguration', renderCell: CopyTextCell },
    { key: 'upToDate', renderCell: UpToDateCell },
    { key: 'checks', renderCell: ChecksCell },
    { key: 'logsS3Link', renderCell: CopyTextCell },
    { key: 'createdAt', valueFormatter: formatDate },
    { key: 'updatedAt', valueFormatter: formatDate },
    { key: 'activationSticksCount', type: 'number', renderCell: ActivationSticksCell },
    { key: 'baseImageS3Link', renderCell: CopyTextCell },
    { key: 'commitHash', renderCell: CopyTextCell },
    { key: 'zymkeySerial', renderCell: CopyTextCell },
    { key: 'rpiSerial', renderCell: CopyTextCell },
    { key: 'username', renderCell: CopyTextCell },
    { key: 'hostname', renderCell: CopyTextCell },
    { key: 'sshPort', type: 'number', renderCell: CopyTextCell },
    { key: 'accessPointSSID', renderCell: CopyTextCell },
    { key: 'vpnProfile', renderCell: CopyTextCell },
    { key: 'vpnIPAddress', renderCell: CopyTextCell },
    { key: 'vpnPubkey', renderCell: CopyTextCell },
    { key: 'bundleDevicePubkey', renderCell: CopyTextCell },
    { key: 'bundleCloudPubkey', renderCell: CopyTextCell },
    { key: 'mapS3Link', renderCell: CopyTextCell },
    { key: 'zymkeyPubkey', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkey', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkeyType', renderCell: CopyTextCell },
    { key: 'deviceSSHPubkeyHash', renderCell: CopyTextCell },
];

export const OnboardDevicesTableGrid = ({
    devices,
    pagination,
    loading,
    error,
    query,
    onQueryChange,
    onlineStatusByDeviceId,
}: OnboardDevicesTableGridProps) => {
    const dispatch = useAppDispatch();
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('devices/onboard');

    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const hardwareConfigurations = useAppSelector(selectOnboardDevicesHardwareConfigurations);
    const [scannedCodes, setScannedCodes] = useState<string[]>([]);
    const gridStateAppliedRef = useRef(false);
    const scannedCodesRef = useRef(scannedCodes);
    scannedCodesRef.current = scannedCodes;
    const gridFilterModelRef = useRef(gridFilterModel);
    gridFilterModelRef.current = gridFilterModel;
    const scannedCodesInitializedRef = useRef(false);

    useMatrixCodeScanner((scannedCode) => {
        setScannedCodes((prev) => (prev.includes(scannedCode) ? prev : [...prev, scannedCode]));
    });

    useEffect(() => {
        dispatch(loadOnboardDevicesHardwareConfigurations());
        dispatch(loadOrganizations());
    }, [dispatch]);

    const sanitizedSortModel = useMemo<GridSortModel>(() => {
        const persistedSortModel = (sortModel || []) as GridSortModel;

        return persistedSortModel.filter((item) => ONBOARD_DEVICE_SERVER_SORT_FIELDS.has(item.field));
    }, [sortModel]);

    const hardwareConfigurationNameById = useMemo(() => {
        const map = new Map<string, string>();
        (hardwareConfigurations ?? []).forEach((configuration) =>
            map.set(configuration.id!, configuration.name),
        );

        return map;
    }, [hardwareConfigurations]);

    const rows = useMemo(() => {
        return buildOnboardDeviceRows(devices, hardwareConfigurationNameById, onlineStatusByDeviceId);
    }, [hardwareConfigurationNameById, onlineStatusByDeviceId, devices]);

    const onReloadOnboardDevices = useCallback(() => {
        dispatch(loadOnboardDevices(query));
    }, [dispatch, query]);

    const columns: GridColDef[] = useMemo(() => {
        return ONBOARD_DEVICE_COLUMN_DEFS.map((col) => {
            const isServerFilterable = isOnboardDeviceServerFilterable(col.key);

            return {
                field: col.key,
                type: (col.type || 'string') as GridColType,
                headerName: col.headerName || transformKeyToTitle(col.key),
                renderCell:
                    col.key === 'routeCard'
                        ? (params: any) => <RoadMapCell {...params} onReload={onReloadOnboardDevices} />
                        : col.renderCell,
                valueFormatter: col.valueFormatter,
                width: columnsWidth[col.key] || 200,
                align: 'left',
                headerAlign: 'left',
                sortable: isOnboardDeviceServerSortable(col.key),
                filterable: isServerFilterable,
                filterOperators: isServerFilterable ? getOnboardDeviceFilterOperators(col.key) : undefined,
            };
        });
    }, [columnsWidth, onReloadOnboardDevices]);

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel) => {
            onQueryChange({
                page: model.page + 1,
                pageSize: model.pageSize,
            });
        },
        [onQueryChange],
    );

    const handleServerSortModelChange = useCallback(
        (model: GridSortModel) => {
            const nextSortModel = model.filter((item) => ONBOARD_DEVICE_SERVER_SORT_FIELDS.has(item.field));

            handleSortModelChange(nextSortModel);
            onQueryChange({
                page: 1,
                ...getOnboardDevicesSortQueryPatch(nextSortModel),
            });
        },
        [handleSortModelChange, onQueryChange],
    );

    const handleServerFilterModelChange = useCallback(
        (model: GridFilterModel) => {
            handleFilterModelChange(model);
            const gridFilters = buildOnboardDevicesFiltersFromGridFilterModel(model);
            onQueryChange({
                page: 1,
                filters: withScannedCodesFilter(gridFilters, scannedCodesRef.current),
            });
        },
        [handleFilterModelChange, onQueryChange],
    );

    useEffect(() => {
        if (!gridStateSynced || gridStateAppliedRef.current) {
            return;
        }

        gridStateAppliedRef.current = true;

        onQueryChange({
            page: 1,
            ...getOnboardDevicesSortQueryPatch(sanitizedSortModel),
            filters: buildOnboardDevicesFiltersFromGridFilterModel(gridFilterModel),
        });
    }, [gridFilterModel, gridStateSynced, onQueryChange, sanitizedSortModel]);

    useEffect(() => {
        if (!scannedCodesInitializedRef.current) {
            scannedCodesInitializedRef.current = true;
            return;
        }
        const gridFilters = buildOnboardDevicesFiltersFromGridFilterModel(gridFilterModelRef.current);
        onQueryChange({
            page: 1,
            filters: withScannedCodesFilter(gridFilters, scannedCodes),
        });
    }, [scannedCodes, onQueryChange]);

    useEffect(() => {
        const nextSelectedRows = getSelectedRowsForDevices(selectedRows, devices);

        if (nextSelectedRows.length !== selectedRows.length) {
            dispatch(setSelectedOnboardDeviceTableRows(nextSelectedRows));
        }
    }, [dispatch, selectedRows, devices]);

    const onResetScannedDevices = useCallback(() => {
        setScannedCodes([]);
    }, []);

    const onRowSelectionModelChange = useCallback(
        (rows: GridRowSelectionModel) => {
            dispatch(setSelectedOnboardDeviceTableRows(rows as string[]));
        },
        [dispatch],
    );

    const Toolbar = useCallback(
        () => (
            <OnboardDevicesToolbar
                devices={devices}
                hasScannedFilter={scannedCodes.length > 0}
                scannedDevicesCount={scannedCodes.length}
                onResetScannedDevices={onResetScannedDevices}
                onReloadOnboardDevices={onReloadOnboardDevices}
            />
        ),
        [devices, onReloadOnboardDevices, onResetScannedDevices, scannedCodes.length],
    );

    return (
        <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {gridStateSynced && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    rowCount={pagination.totalItems}
                    paginationModel={{
                        page: Math.max(query.page - 1, 0),
                        pageSize: query.pageSize,
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPaginationModelChange={handlePaginationModelChange}
                    slots={{
                        toolbar: Toolbar as GridSlots['toolbar'],
                    }}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={selectedRows}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onCellKeyDown={onCellKeyDown}
                    filterModel={gridFilterModel}
                    sortModel={sanitizedSortModel}
                    onSortModelChange={handleServerSortModelChange}
                    onColumnWidthChange={handleColumnWidthChange}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onFilterModelChange={handleServerFilterModelChange}
                    columnVisibilityModel={columnsConfig}
                />
            )}
        </>
    );
};
